import { type JSX, useReducer } from "react";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import { Button } from "common/components/ui/Button";
import WorkspaceAccountSelector from "common/components/WorkspaceAccountSelector";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { ProfileSearchParams } from "common/types";

import { DEFAULT_SEARCH_SOURCE_PARAMS } from "../../../constants";
import useSearchSourceLeads from "../../../datahooks/useSearchSourceLeads";
import useSelectedMinerAccount from "../../../hooks/useSelectedMinerAccount";
import ScrollAndFooter from "../ScrollAndFooter";
import DesktopFilters from "./Filters/DesktopFilters";
import MobileFilters from "./Filters/MobileFilters";
import Keywords from "./Keywords";
import PasteUrlTargeting from "./PasteUrlTargeting";
import PreviewResults from "./PreviewResults";

import type { SearchLeadListSource } from "../../../types";

import ErrorPlaceholderImage from "assets/images/empty-placeholders/error.png";

export default function Search({
  createSource,
  isCreatingSource,
}: {
  createSource: (params: {
    miner: string;
    settings: SearchLeadListSource["settings"];
  }) => void;
  isCreatingSource: boolean;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const { minerAccountId, setMinerAccountId } = useSelectedMinerAccount();

  const [searchParams, setSearchParams] = useReducer(
    (
      prevState: ProfileSearchParams,
      newState: Partial<ProfileSearchParams>,
    ) => ({
      ...prevState,
      ...newState,
    }),
    DEFAULT_SEARCH_SOURCE_PARAMS,
  );

  const {
    profiles,
    isFetchingProfiles,
    totalCount,
    isLimitExceeded,
    isProfilesPreviewError,
    refetchProfilesPreview,
  } = useSearchSourceLeads({ accountId: minerAccountId, searchParams });

  let content: JSX.Element;

  if (isProfilesPreviewError) {
    content = (
      <ErrorPlaceholder
        errorMessage="Error loading search preview results"
        onRetry={refetchProfilesPreview}
      />
    );
  } else if (isLimitExceeded) {
    content = (
      <EmptyPlaceholder
        imageSrc={ErrorPlaceholderImage}
        title="LinkedIn search limit reached"
        subtitle="Try again later, or select a different search account"
        actionComponent={
          <Button variant="secondary-black" asChild>
            <a
              href={
                getWhitelabelCustomization()
                  ? "https://help.lnconnect.me/en/articles/13619474-linkedin-search-limits"
                  : "https://help.aimfox.com/en/articles/13619474-linkedin-search-limits"
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              Read more
            </a>
          </Button>
        }
      />
    );
  } else {
    content = (
      <PreviewResults
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        profiles={profiles}
        isLoading={isFetchingProfiles}
        totalCount={totalCount}
      />
    );
  }

  const FiltersComponent = isDesktop ? DesktopFilters : MobileFilters;

  const filtersComponent = (
    <FiltersComponent
      accountId={minerAccountId}
      searchParams={searchParams}
      setSearchParams={setSearchParams}
    />
  );
  const keywordsComponent = (
    <Keywords
      keywords={searchParams.keywords}
      setSearchParams={setSearchParams}
    />
  );

  return (
    <ScrollAndFooter
      leadCount={totalCount}
      isCreatingSource={isCreatingSource}
      onCreate={() =>
        createSource({
          settings: { type: "search", ...searchParams },
          miner: minerAccountId,
        })
      }
    >
      <div className="flex gap-x-4 grow">
        {isDesktop && filtersComponent}
        <div className="flex grow flex-col gap-y-4">
          <div className="flex items-start gap-x-2.5">
            <div className="flex items-center gap-1">
              <span className="lg:hidden text-button-xs text-nowrap ml-1">
                Search by:
              </span>
              <WorkspaceAccountSelector
                selection={minerAccountId}
                setSelection={setMinerAccountId}
                size="sm"
                align="start"
              />
            </div>
            {isDesktop && <div className="grow">{keywordsComponent}</div>}
            <div className="flex w-px bg-black-200 h-8 lg:hidden" />
            <PasteUrlTargeting
              accountId={minerAccountId}
              setSearchParams={setSearchParams}
            />
          </div>
          <div className="lg:hidden flex items-start gap-x-2">
            <div className="grow">{keywordsComponent}</div>
            {filtersComponent}
          </div>
          {content}
        </div>
      </div>
    </ScrollAndFooter>
  );
}
